import { ChangeDetectorRef, Component, ElementRef, forwardRef, Inject, NgZone, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { DomSanitizer, SafeHtml }                                                                  from '@angular/platform-browser';
import { ActivatedRoute, Router }                                                                  from '@angular/router';
import { WaitingForResponse }                                                                      from '@cs/common';
import { CsToastManagerService }                                                                   from '@cs/components/toast-manager';
import { CsHttpRequestOptions, LoggerUtil }                                                        from '@cs/core';
import { LoginConfigService, LoginService }                                                        from '@cs/performance-manager/login';
import { AuthenticationService }                                                                   from '@cs/performance-manager/shared';
import { TranslateService }                                                                        from '@ngx-translate/core';
import { take, tap }                                                                               from 'rxjs/operators';
import { ResetPasswordConfigService }                                                              from '../reset-password-config.service';


declare var grecaptcha: any;

@Component({
			   standalone: false,
			   selector:    'pmc-step-one',
			   templateUrl: './step-one.component.html'
		   })
export class StepOneComponent implements OnInit, OnDestroy {

	/**
	 * Binding to the username field
	 */
	username     = '';
	/**
	 * Binding to the captcha field
	 */
	captcha      = '';
	/**
	 * The state of the form. Defaults to true because it's empty
	 */
	isErrorState = true;

	/**
	 * HTML of the Captcha
	 */
	captchaHtml: SafeHtml;

	/**
	 * Indicator for the loader
	 */
	isLoadingCaptcha = true;

	//#region Loaders

	/**
	 * Loader flag for the reset password button
	 */
	isWaitingForReset = false;

	//#endregion
	/**
	 * Flag indicating to load the captcha
	 */
	useGoogleCaptcha = false;

	private get alreadyInjectedDependency() {
		return document.getElementById('googleCaptcha') != null;
	}

	constructor(private sanitizer: DomSanitizer,
				@Inject(forwardRef(() => LoginService)) private loginService: LoginService,
				@Inject(ResetPasswordConfigService) private resetPasswordConfig: ResetPasswordConfigService,
				@Inject(forwardRef(() => LoginConfigService)) public loginConfig: LoginConfigService,
				private l8n: TranslateService,
				private toastService: CsToastManagerService,
				private authenticationService: AuthenticationService,
				private renderer: Renderer2,
				private changeRef: ChangeDetectorRef,
				private route: ActivatedRoute,
				private ngzone: NgZone,
				private router: Router,
				private element: ElementRef) {
		window['onloadRecaptchaCallback'] = () => {
			this.refreshCaptcha();
		};

		// Receives the token once the (invisible) challenge completes. The form is
		// already validated in submitWithCaptcha() before the challenge runs.
		window['submitForm'] = (token) => this.ngzone.run(() => {
			this.captcha	= token;
			this.resetPassword();
		});

		this.l8n.get('RESTORE_ACCOUNT')
			.subscribe(value => this.loginService.setTitleMessage(value));
		loginService.setUnderTitleMessage('');

	}

	ngOnInit() {
		this.resetPasswordConfig.initiatePasswordReset()
			.subscribe((result) => {
				this.isLoadingCaptcha     = false;
				const injectedRootUrlHtml = result.value.captchaHtml;
				this.useGoogleCaptcha     = injectedRootUrlHtml ? result.value.requiresGoogleCaptcha || true : result.value.requiresGoogleCaptcha || false;
				this.captchaHtml          = this.sanitizer.bypassSecurityTrustHtml(injectedRootUrlHtml);
				this.injectDependency();
			});
	}

	ngOnDestroy(): void {
		this.captchaHtml = '';
		this.changeRef.detectChanges();
	}

	/**
	 * Id of the reCAPTCHA widget rendered by refreshCaptcha() (render=explicit —
	 * we own the only render). Used to execute() the challenge from
	 * submitWithCaptcha() and to reset() a consumed token (COH-3297).
	 */
	private captchaWidgetId: number | null = null;

	/**
	 * Tear down the current reCAPTCHA widget and build a fresh one. Used after a
	 * failed attempt instead of grecaptcha.reset(): resetting an invisible widget
	 * rejects its internal promises and can leave the next execute() silently
	 * dead (COH-3297). Recreating the container via the [innerHtml] binding
	 * yields a virgin element, so refreshCaptcha() renders a brand-new widget.
	 */
	private rebuildCaptcha() {
		if (!this.useGoogleCaptcha) return;

		const captchaMarkup = this.captchaHtml;
		this.captchaHtml    = '';
		this.changeRef.detectChanges();
		document.querySelector('.grecaptcha-badge')?.remove();
		this.captchaWidgetId = null;
		this.captchaHtml     = captchaMarkup;
		this.changeRef.detectChanges();
		this.refreshCaptcha();
	}

	refreshCaptcha() {
		setTimeout(() => {
			const container = this.element.nativeElement.querySelector('.reset-password .g-recaptcha');
			if (container) {
				try {
					// Rendered-ness is stamped on the container ELEMENT, not inferred from
					// child count — the invisible widget's badge may be re-parented, which
					// would make the container look "empty" and cause a second render on
					// the same element, corrupting grecaptcha's internals (COH-3297).
					if (container.getAttribute('data-cs-rendered') !== 'true') {
						// Fresh (or recreated) container: render our own widget. A badge
						// left behind by a previous widget instance is orphaned — remove it.
						if (this.captchaWidgetId !== null) {
							document.querySelector('.grecaptcha-badge')?.remove();
						}
						// data-bind is stripped so grecaptcha does NOT also bind the submit
						// button — a bound widget goes dead after grecaptcha.reset();
						// submitWithCaptcha() drives execute() explicitly instead.
						container.removeAttribute('data-bind');
						this.captchaWidgetId = grecaptcha.render(container, {
							'sitekey': container.getAttribute('data-sitekey'),
							// The server div declares no data-size; explicit render then
							// defaults to the visible checkbox, so pin the invisible mode
							// this flow is designed for (execute() from submitWithCaptcha).
							'size':    container.getAttribute('data-size') ?? 'invisible',
							'callback': (output) => {
								window['submitForm'](output);
							}
						});
						container.setAttribute('data-cs-rendered', 'true');
					} else if (this.captchaWidgetId !== null) {
						// Live widget: clear the consumed/expired token.
						grecaptcha.reset(this.captchaWidgetId);
					}
				} catch (e) {
					LoggerUtil.log(`Failed to render/reset the captcha: ${e?.message ?? e}`);
				}
			}
			this.changeRef.detectChanges();
		}, 0);
		this.changeRef.detectChanges();
	}

	/**
	 * Submit entry point for the captcha flow (button click): validate the form
	 * first, then programmatically run the invisible challenge. The token arrives
	 * in window.submitForm, which performs the actual reset request.
	 */
	submitWithCaptcha() {
		const form = document.getElementById('form') as HTMLFormElement;
		if (form && !form.checkValidity()) {
			form.reportValidity();
			return;
		}

		if (this.captchaWidgetId !== null) {
			try {
				grecaptcha.execute(this.captchaWidgetId);
			} catch (e) {
				LoggerUtil.log(`Failed to execute the captcha: ${e?.message ?? e}`);
			}
		} else {
			// Widget never rendered (script/network hiccup) — try to recover.
			this.refreshCaptcha();
		}
	}

	/**
	 * Send the password request to the server, it contains the captcha and email
	 */
	resetPassword() {

		const options: CsHttpRequestOptions = {
			errorResponseHandler: (error): boolean => {
				// Handle documented 400 error
				if (error && (error.status === 401 || error.status === 400)) {
					this.toastService.show({
											   title:   this.l8n.instant('ERROR_SOMETHING_WRONG'),
											   content: error.statusText,
											   type:    'error'
										   });

					// Rebuild — not grecaptcha.reset(): resetting an invisible widget can
					// leave the next execute() dead (COH-3297).
					this.rebuildCaptcha();
					return true;
				}
			},
			headers:              null
		};

		this.resetPasswordConfig.requestPasswordReset(this.username, this.captcha, options)
			.pipe(tap(WaitingForResponse.new(isLoading => this.isWaitingForReset = isLoading)))
			.subscribe((result) => {
				this.router.navigate(['verify-reset'], {relativeTo: this.route.parent});
			});
	}

	getErrorMessage() {
		if (this.isLoadingCaptcha) {
			return this.l8n.instant('CAPTCHA_STILL_LOADING');
		} else if (this.username.length === 0) {
			return this.l8n.instant('NO_USERNAME');
		}
	}

	checkErrorState() {
		this.isErrorState = (this.useGoogleCaptcha && this.captcha.length === 0) || this.username.length === 0 || this.isLoadingCaptcha;
	}


	goBackOfLogout() {
		this.authenticationService.logOut();
	}

	private injectDependency() {

		if (this.alreadyInjectedDependency) {
			this.refreshCaptcha();
			return;
		}

		const script   = document.createElement('script');
		script.id      = 'googleCaptcha';
		// render=explicit: WE render the widget (refreshCaptcha) — auto-render would
		// bind the button via data-bind, and a bound widget breaks after a reset().
		script.src     = 'https://www.google.com/recaptcha/api.js?onload=onloadRecaptchaCallback&render=explicit';
		script.type    = 'text/javascript';
		script.async   = true;
		script.defer   = true;
		script.charset = 'utf-8';
		this.renderer.appendChild(document.head, script);
	}
}
